import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DEFAULTVALUE from '@/app/constants/defaultValue';
import { SubGroup } from '@/app/models/financalGroup';
import { TransferDetailBase } from '@/app/models/transferDetail';
import { useTravelTransferMutation } from '@/app/services/detailsTravelApi';
import { formatDateForBackend } from '@/app/utils/dateUtils';
import { formatCurrency } from '@/app/utils/numberUtils';
import DrawerButtons from '@/components/common/DrawerButtons';
import { usePeriod } from '@/context/PeriodContext';
import useFormattedCurrency from '@/hooks/useFormattedCurrency';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import FormField from '../baseForms/FormField';
import FormFieldLong from '../baseForms/FormFieldLong';
import FormFieldQuantity from '../baseForms/FormFieldQuantity';

interface NewTravelTransferProps {
  selectedItem: TransferDetailBase;
  setDrawerOpen: (open: boolean) => void;
  selectedFinancialSubGroup: SubGroup;
}

export default function NewTravelTransfer(props: NewTravelTransferProps) {
  const { t, i18n } = useTranslation(['travel', 'common']);

  const theme = useTheme();
  const { selectedPeriod } = usePeriod();
  const { selectedItem, setDrawerOpen, selectedFinancialSubGroup } = props;

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const { value: totalValue, setValue: setTotalValue } = useFormattedCurrency(selectedItem?.newDetail.value);

  const [quantity, setQuantity] = useState<number | null>(null);
  const [plannedInitDate, setPlannedInitDate] = useState<string>('');
  const [plannedEndDate, setPlannedEndDate] = useState<string>('');

  const [objecives, setObjecives] = useState<string | null>('');

  const [local, setLocal] = useState<string | null>('');
  const [justification, setJustification] = useState<string | null>('');

  const [transfer] = useTravelTransferMutation();

  const handleSubmit = async () => {
    if (!selectedItem) return;
    setDrawerOpen(false);

    await toast.promise(
      transfer({
        id: selectedItem.id,
        originFinancialGroupCode: selectedItem.originFinancialGroupCode as string,
        financialSubGroupId: selectedFinancialSubGroup.id as number,
        period: selectedPeriod?.date as string,
        newDetail: {
          objective: objecives as string,
          value: parseFloat(totalValue.replace('R$', '').replace('.', '').replace(',', '.')) || DEFAULTVALUE,
          quantity: quantity || DEFAULTVALUE,
          initDate: formatDateForBackend(plannedInitDate),
          endDate: formatDateForBackend(plannedEndDate),
          local: local as string,
          justify: justification as string,
        },
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:success.transfer') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const hasNegative = val.includes('-');
    const cleaned = val.replace(/[^0-9]/g, '');
    const raw = hasNegative ? `-${cleaned}` : cleaned;
    setTotalValue(formatCurrency(raw));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(80vh - 220px)',
        maxHeight: 'calc(80vh - 220px)',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        />
        <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 2 }}>
          <FormField label={t('common:total.value') as string} value={totalValue} onChange={handleValueChange} />

          <FormField
            label={t('common:objective') as string}
            value={objecives as string}
            onChange={(e) => setObjecives(e.target.value)}
            rows={3}
            multiline
          />

          <FormFieldQuantity label={t('common:quantity')} value={quantity} onChange={setQuantity} />

          <FormField label={t('local') as string} value={local as string} onChange={(e) => setLocal(e.target.value)} />

          <Box sx={{ mt: 4 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" width={theme.spacing(15)}>
                  {t('common:start')}
                </Typography>
                <Stack direction="row" sx={{ width: '30%' }}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={plannedInitDate ? dayjs(plannedInitDate, 'DD/MM/YYYY') : null}
                    onChange={(newValue: Dayjs | null) =>
                      setPlannedInitDate(newValue ? newValue.format('DD/MM/YYYY') : '')
                    }
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                      },
                    }}
                    slots={{ openPickerIcon: () => null }}
                  />
                </Stack>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                <Typography variant="fieldHeader" width={theme.spacing(15)}>
                  {t('common:end')}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ width: '30%' }}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={plannedEndDate ? dayjs(plannedEndDate, 'DD/MM/YYYY') : null}
                    onChange={(newValue: Dayjs | null) =>
                      setPlannedEndDate(newValue ? newValue.format('DD/MM/YYYY') : '')
                    }
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } },
                      },
                    }}
                    slots={{ openPickerIcon: () => null }}
                  />
                </Stack>
              </Box>
            </LocalizationProvider>
          </Box>

          <FormFieldLong
            label={t('common:justification')}
            value={justification as string}
            onChange={(e) => setJustification(e.target.value)}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 'auto', mb: 4 }}>
        <DrawerButtons
          onSave={() => {
            handleSubmit();
          }}
          onCancel={handleCancel}
          labelSave={t('common:transfer') as string}
        />
      </Box>
    </Box>
  );
}
